import { onAuthStateChanged } from "firebase/auth";
import { auth, firestore } from "../firebase-config";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";

export const light_mode_secondary = 'bg-white';
export const light_mode_primary = 'bg-[#F5F5F7]';
export const dark_mode_hover = 'hover:bg-[#0E182C]';
export const dark_mode_primary = 'bg-[#030A15] text-white';
export const dark_mode_secondary = 'bg-[#05101E] text-[#E2E8F0]';

export const AppMode = {
    LIGHT: 0,
    DARK: 1,
};

export const AuthStatus = {
    NOT_DETERMINED: 0,
    NOT_LOGGED_IN: 1,
    LOGGED_IN: 2,
};

export const TaskStatus = {
    TODO: 'todo',
    IN_PROGRESS: 'inProgress',
    DONE: 'done',
};

export const Priority = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
};

export const ProviderAuthStatus = {
    NOT_DETERMINED: 0,
    NOT_LOGGED_IN: 1,
    LOGGED_IN: 2,
};

export const PaymentTiers = {
    FREE: 0,
    STARTER: 1,
    PRO: 2,
    ENTERPRISE: 3,
};

export const AccountLimits = {
    [PaymentTiers.FREE]: 2,
    [PaymentTiers.STARTER]: 5,
    [PaymentTiers.PRO]: 10,
    [PaymentTiers.ENTERPRISE]: 15,
};

export const TierNames = {
    [PaymentTiers.FREE]: 'Free',
    [PaymentTiers.STARTER]: 'Starter',
    [PaymentTiers.PRO]: 'Pro',
    [PaymentTiers.ENTERPRISE]: 'Enterprise',
};

export const GoogleAuth = {
    NOT_DETERMINED: 0,
    NOT_LOGGED_IN: 1,
    LOGGED_IN: 2,
};

export const MicrosoftAuth = {
    NOT_DETERMINED: 0,
    NOT_LOGGED_IN: 1,
    LOGGED_IN: 2,
};

export const AuthProvider = {
    GOOGLE: 'google',
    MICROSOFT: 'microsoft',
};

export const oAuthStatus = {
    NOT_DETERMINED: 0,
    NOT_LOGGED_IN: 1,
    LOGGED_IN: 2,
};

export class AuthAccount {
    constructor(id, provider, status = oAuthStatus.NOT_DETERMINED, userData = null) {
        this.id = id;
        this.status = status;
        this.provider = provider;
        this.userData = userData;
    }
}

export class AuthManager {
    constructor() {
        this.accounts = [];
        this.accessTokens = new Map();
        this.refreshListeners = new Set();
        this.subscriptionTier = PaymentTiers.FREE;
        this.activeAccounts = new Map();
        this.firebaseUser = null;
        this.firestoreUnsubscribe = null;
        this.initializeFromStorage();
        this.authStateListener = onAuthStateChanged(auth, (user) => {
            this.firebaseUser = user;
            if (user) {
                this.initializeFromFirestore(user.uid);
            } else {
                if (this.firestoreUnsubscribe) {
                    this.firestoreUnsubscribe();
                    this.firestoreUnsubscribe = null;
                }
            }
        });
    }

    async initializeFromFirestore(userId) {
        try {
            const userDocRef = doc(firestore, 'users', userId);
            if (this.firestoreUnsubscribe) {
                this.firestoreUnsubscribe();
            }
            this.firestoreUnsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const userData = docSnapshot.data();
                    let newTier = PaymentTiers.FREE;
                    if (userData.isStarter) newTier = PaymentTiers.STARTER;
                    if (userData.isPro) newTier = PaymentTiers.PRO;
                    if (userData.isEnterprise) newTier = PaymentTiers.ENTERPRISE;
                    if (this.subscriptionTier !== newTier) {
                        this.subscriptionTier = newTier;
                        localStorage.setItem('SubscriptionTier', newTier.toString());
                        this.notifyStateChange();
                    }
                }
            }, (error) => { });
            const docSnap = await getDoc(userDocRef);
            if (docSnap.exists()) {
                const userData = docSnap.data();
                if (userData.isStarter) this.subscriptionTier = PaymentTiers.STARTER;
                else if (userData.isPro) this.subscriptionTier = PaymentTiers.PRO;
                else if (userData.isEnterprise) this.subscriptionTier = PaymentTiers.ENTERPRISE;
                else this.subscriptionTier = PaymentTiers.FREE;
                localStorage.setItem('SubscriptionTier', this.subscriptionTier.toString());
                this.notifyStateChange();
            }
        } catch (error) { }
    }

    initializeFromStorage() {
        const savedTier = localStorage.getItem('SubscriptionTier');
        if (savedTier !== null) {
            this.subscriptionTier = parseInt(savedTier, 10);
        }
        const savedAccounts = localStorage.getItem('AuthAccounts');
        if (savedAccounts) {
            try {
                this.accounts = JSON.parse(savedAccounts).map(accountData => {
                    if (accountData.userData) {
                        const { accessToken, tokenExpiry, ...safeUserData } = accountData.userData;
                        return new AuthAccount(accountData.id, accountData.provider, accountData.status, safeUserData);
                    }
                    return new AuthAccount(accountData.id, accountData.provider, accountData.status, accountData.userData);
                });
                this.accounts.forEach(account => {
                    if (account.status === oAuthStatus.LOGGED_IN) {
                        this.activeAccounts.set(account.provider, account.id);
                    }
                });
            } catch (error) {
                this.accounts = [];
            }
        } else {
            this.migrateFromLegacyStorage();
        }
        const savedTokens = localStorage.getItem('AuthTokens');
        if (savedTokens) {
            try {
                const tokenData = JSON.parse(savedTokens);
                Object.entries(tokenData).forEach(([accountId, data]) => {
                    this.accessTokens.set(accountId, {
                        token: data.token,
                        expiresAt: data.expiresAt,
                    });
                });
            } catch (error) { }
        }
    }

    migrateFromLegacyStorage() {
        const migrateProvider = (providerKey, userDataKey, providerType) => {
            const status = localStorage.getItem(providerKey);
            if (status && JSON.parse(status) === ProviderAuthStatus.LOGGED_IN) {
                const userData = localStorage.getItem(userDataKey);
                if (userData) {
                    try {
                        const parsedUserData = JSON.parse(userData);
                        const { accessToken, tokenExpiry, ...safeUserData } = parsedUserData;
                        const account = this.addAccount(providerType, safeUserData);
                        if (accessToken) {
                            this.setAccessToken(
                                account.id,
                                accessToken,
                                tokenExpiry ? (tokenExpiry - Date.now()) / 1000 : 3600,
                            );
                        }
                        this.activeAccounts.set(providerType, account.id);
                    } catch (error) { }
                }
            }
        };
        migrateProvider('GoogleAuth', 'GoogleUserData', AuthProvider.GOOGLE);
        migrateProvider('MicrosoftAuth', 'MicrosoftUserData', AuthProvider.MICROSOFT);
        if (this.accounts.length > 0) {
            this.persistAccounts();
            ['GoogleAuth', 'GoogleUserData', 'MicrosoftAuth', 'MicrosoftUserData']
                .forEach(key => localStorage.removeItem(key));
        }
    }

    canAddMoreAccounts() {
        const accountLimit = AccountLimits[this.subscriptionTier];
        const currentAccountCount = this.getLoggedInAccounts().length;
        return currentAccountCount < accountLimit;
    }

    addAccount(provider, userData) {
        if (!this.canAddMoreAccounts()) {
            throw new Error(`Account limit reached for your ${TierNames[this.subscriptionTier]} plan. Please upgrade to add more accounts.`);
        }
        const providerUniqueId = this.getProviderUniqueId(provider, userData);
        if (!providerUniqueId) {
            throw new Error(`Failed to generate unique ID for ${provider} account`);
        }

        const id = `${provider}-${providerUniqueId}`;
        const existingAccount = this.accounts.find(acc => acc.id === id);
        if (existingAccount) {
            const { accessToken, tokenExpiry, ...safeUserData } = userData;
            existingAccount.userData = { ...existingAccount.userData, ...safeUserData };
            existingAccount.status = ProviderAuthStatus.LOGGED_IN;
            if (accessToken) {
                this.accessTokens.set(id, {
                    token: accessToken,
                    expiresAt: tokenExpiry || Date.now() + 3600000,
                });
                this.persistTokens();
            }
            this.activeAccounts.set(provider, id);
            this.persistAccounts();
            this.notifyStateChange();
            return existingAccount;
        }

        const { accessToken, tokenExpiry, ...safeUserData } = userData;
        const account = new AuthAccount(id, provider, ProviderAuthStatus.LOGGED_IN, safeUserData);

        if (accessToken) {
            this.accessTokens.set(id, {
                token: accessToken,
                expiresAt: tokenExpiry || Date.now() + 3600000,
            });
            this.persistTokens();
        }
        this.accounts.push(account);
        this.activeAccounts.set(provider, id);
        this.persistAccounts();
        this.notifyStateChange();
        return account;
    }

    getProviderUniqueId(provider, userData) {
        if (provider === AuthProvider.GOOGLE) {
            return userData.id || userData.sub || userData.email;
        } else if (provider === AuthProvider.MICROSOFT) {
            return userData.id || userData.localAccountId || userData.homeAccountId || userData.email;
        }
        return userData.email || Date.now().toString();
    }

    async setSubscriptionTier(tier) {
        this.subscriptionTier = tier;
        localStorage.setItem('SubscriptionTier', tier.toString());
        if (this.firebaseUser) {
            try {
                const userDocRef = doc(firestore, 'users', this.firebaseUser.uid);
                const tierData = {
                    isStarter: tier === PaymentTiers.STARTER,
                    isPro: tier === PaymentTiers.PRO,
                    isEnterprise: tier === PaymentTiers.ENTERPRISE,
                    subscriptionTier: tier
                };
                await updateDoc(userDocRef, tierData);
            } catch (error) { }
        }
        this.notifyStateChange();
        return this.subscriptionTier;
    }

    getSubscriptionInfo() {
        return {
            tier: this.subscriptionTier,
            name: TierNames[this.subscriptionTier],
            accountLimit: AccountLimits[this.subscriptionTier],
            accountsUsed: this.getLoggedInAccounts().length,
            canAddMore: this.canAddMoreAccounts(),
        };
    }

    getAccessToken(accountId) {
        const tokenData = this.accessTokens.get(accountId);
        if (!tokenData) return null;
        if (tokenData.expiresAt < Date.now()) {
            return null;
        }
        return tokenData.token;
    }

    getTokenInfo(accountId) {
        try {
            const tokens = JSON.parse(localStorage.getItem('AuthTokens') || '{}');
            return tokens[accountId] || null;
        } catch (error) {
            return null;
        }
    }

    scopesIncluded(existingScopes, requiredScopes) {
        if (!existingScopes || !requiredScopes) return false;
        const existingScopesArray = Array.isArray(existingScopes) ? existingScopes : [existingScopes];
        const requiredScopesArray = Array.isArray(requiredScopes) ? requiredScopes : [requiredScopes];
        return requiredScopesArray.every(scope =>
            existingScopesArray.some(existingScope =>
                existingScope.toLowerCase() === scope.toLowerCase() ||
                existingScope.toLowerCase() === 'mail.readwrite.all' ||
                existingScope.toLowerCase() === 'mail.send.all' ||
                (scope.toLowerCase() === 'mail.send' && existingScope.toLowerCase() === 'mail.readwrite') ||
                (scope.toLowerCase() === 'mail.read' && existingScope.toLowerCase() === 'mail.readwrite')
            )
        );
    }

    hasRequiredScopes(accountId, requiredScopes) {
        const tokenInfo = this.getTokenInfo(accountId);
        if (!tokenInfo || !tokenInfo.scopes) return false;
        return this.scopesIncluded(tokenInfo.scopes, requiredScopes);
    }

    setAccessToken(accountId, token, expiresIn, scopes) {
        if (!accountId || !token || !expiresIn) {
            throw new Error("Invalid token information");
        }
        const expiresAt = Date.now() + (expiresIn * 1000);
        const tokenInfo = { token, expiresAt, scopes: scopes || [] };
        this.accessTokens.set(accountId, { token, expiresAt, scopes });
        let tokens = {};
        try {
            const storedTokens = localStorage.getItem('AuthTokens');
            if (storedTokens) {
                tokens = JSON.parse(storedTokens);
            }
        } catch (error) {
            tokens = {};
        }
        tokens[accountId] = {
            ...tokens[accountId],
            ...tokenInfo
        };
        localStorage.setItem('AuthTokens', JSON.stringify(tokens));
        return true;
    }

    persistTokens() {
        try {
            const tokenData = {};
            this.accessTokens.forEach((value, key) => {
                tokenData[key] = {
                    token: value.token,
                    expiresAt: value.expiresAt,
                    scopes: value.scopes || [],
                };
            });
            localStorage.setItem('AuthTokens', JSON.stringify(tokenData));
        } catch (error) { }
    }

    removeAccount(accountId) {
        const account = this.accounts.find(acc => acc.id === accountId);
        if (!account) return false;
        this.accessTokens.delete(accountId);
        this.persistTokens();
        this.accounts = this.accounts.filter(account => account.id !== accountId);
        for (const [provider, activeId] of this.activeAccounts.entries()) {
            if (activeId === accountId) {
                const remainingAccounts = this.getAccounts(provider);
                if (remainingAccounts.length > 0) {
                    this.activeAccounts.set(provider, remainingAccounts[0].id);
                } else {
                    this.activeAccounts.delete(provider);
                }
                break;
            }
        }
        this.persistAccounts();
        this.notifyStateChange();
        return true;
    }

    getAccounts(provider = null) {
        return provider
            ? this.accounts.filter(account => account.provider === provider)
            : this.accounts;
    }

    getLoggedInAccounts() {
        return this.accounts.filter(account => account.status === ProviderAuthStatus.LOGGED_IN);
    }

    getActiveAccount(provider) {
        const activeId = this.activeAccounts.get(provider);
        if (activeId) {
            return this.accounts.find(acc => acc.id === activeId);
        }
        const accounts = this.getAccounts(provider);
        return accounts.find(acc => acc.status === ProviderAuthStatus.LOGGED_IN);
    }

    setActiveAccount(accountId) {
        const account = this.accounts.find(acc => acc.id === accountId);
        if (!account) return false;
        this.activeAccounts.set(account.provider, accountId);
        this.notifyStateChange();
        return true;
    }

    persistAccounts() {
        try {
            const safeAccounts = this.accounts.map(account => {
                return {
                    id: account.id,
                    provider: account.provider,
                    status: account.status,
                    userData: account.userData ? { ...account.userData, accessToken: undefined, tokenExpiry: undefined } : null
                };
            });
            localStorage.setItem('AuthAccounts', JSON.stringify(safeAccounts));
            const activeAccountsData = {};
            this.activeAccounts.forEach((value, key) => {
                activeAccountsData[key] = value;
            });
            localStorage.setItem('ActiveAuthAccounts', JSON.stringify(activeAccountsData));
        }
        catch (error) { }
    }

    updateAccountStatus(accountId, newStatus) {
        const account = this.accounts.find(a => a.id === accountId);
        if (account) {
            account.status = newStatus;
            this.persistAccounts();
            this.notifyStateChange();
        }
    }

    updateUserData(accountId, newUserData) {
        const account = this.accounts.find(a => a.id === accountId);
        if (account) {
            const { accessToken, tokenExpiry, ...safeUserData } = newUserData;
            account.userData = { ...account.userData, ...safeUserData };
            if (accessToken) {
                this.setAccessToken(
                    accountId,
                    accessToken,
                    tokenExpiry ? (tokenExpiry - Date.now()) / 1000 : 3600,
                );
            }
            this.persistAccounts();
            this.notifyStateChange();
        }
    }

    clearAllAccounts() {
        this.accessTokens.clear();
        this.persistTokens();
        this.accounts = [];
        this.activeAccounts.clear();
        this.persistAccounts();
        this.notifyStateChange();
    }

    addStateChangeListener(callback) {
        if (typeof callback === 'function') {
            this.refreshListeners.add(callback);
            return true;
        }
        return false;
    }

    clearAccessToken(accountId) {
        try {
            const tokens = JSON.parse(localStorage.getItem('AuthTokens') || '{}');
            if (tokens[accountId]) {
                delete tokens[accountId];
                localStorage.setItem('AuthTokens', JSON.stringify(tokens));
            }
            return true;
        } catch (error) {
            return false;
        }
    }

    removeStateChangeListener(callback) {
        return this.refreshListeners.delete(callback);
    }

    notifyStateChange() {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('authStateChanged'));
        }
        this.refreshListeners.forEach(callback => {
            try {
                callback();
            } catch (error) { }
        });
    }

    cleanup() {
        if (this.authStateListener) {
            this.authStateListener();
        }
        if (this.firestoreUnsubscribe) {
            this.firestoreUnsubscribe();
        }
    }
}

export const authManagerInstance = new AuthManager();

export const featuresRTL = [
    'Real-time updates across tasks, emails, and calendars',
    'Quick, unified search to find everything you need in seconds',
    'AI-driven insights for rapid decision-making',
];

export const profileOptions = [
    { title: 'Your Orders', desc: 'Track, return, or reorder', icon: <i className="fa-solid fa-box-open text-3xl"></i>, onTap: () => { window.location.href = '/orders' } },
    { title: 'Your Addresses', desc: 'Edit Addresses for orders', icon: <i className="fa-solid fa-location-dot text-3xl"></i>, onTap: () => { window.location.href = '/addresses' } },
    { title: 'Contact Us', desc: 'Contact us via phone or email', icon: <i className="fa-regular fa-user text-3xl"></i>, onTap: () => { window.location.href = '/contact' } },
    { title: 'Log Out', desc: 'Log out of your account', icon: <i className="fa-solid fa-arrow-right-from-bracket text-3xl"></i>, onTap: () => { } },
];

export const featuresLTR = [
    'Intuitive interface that feels like second nature',
    'Simple drag-and-drop task organization',
    'Hassle-free onboarding with minimal setup time',
];
export const faqs = [
    { question: 'What is Corella?', answer: 'Answer' },
    { question: 'Is it easy to learn?', answer: 'Answer' },
    { question: 'How do I get started?', answer: 'Answer' },
    { question: 'Is my data secure with Corella?', answer: 'Answer' },
    { question: 'How does the AI feature assist me?', answer: 'Answer' },
    { question: 'Do you offer a free trial, or a freemium plan?', answer: 'Answer' },
    { question: 'Which tools and platforms does Corella integrate with?', answer: 'Answer' },
    { question: 'Is Corella suitable for both small teams and large enterprises?', answer: 'Answer' },
];

export const colors = [
    { bg: 'bg-blue-50', border: 'border-blue-600', text: 'text-blue-600' },
    { bg: 'bg-green-50', border: 'border-[#546FFF]', text: 'text-[#546FFF]' },
    { bg: 'bg-purple-50', border: 'border-purple-600', text: 'text-purple-600' },
    { bg: 'bg-yellow-50', border: 'border-yellow-600', text: 'text-yellow-600' },
    { bg: 'bg-red-50', border: 'border-red-600', text: 'text-red-600' },
    { bg: 'bg-indigo-50', border: 'border-indigo-600', text: 'text-indigo-600' },
];

export const data = {
    labels: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    datasets: [
        {
            fill: true,
            tension: 0.4,
            label: 'Task',
            borderWidth: 3,
            pointRadius: 5,
            pointHoverRadius: 8,
            borderColor: '#1F1F1F',
            pointBorderColor: '#fff',
            pointHoverBorderColor: '#fff',
            pointBackgroundColor: '#4F46E5',
            data: [1, 2, 1.5, 3, 2.5, 2, 1.8],
            pointHoverBackgroundColor: '#4F46E5',
            backgroundColor: 'rgba(31, 31, 31, 0.1)',
        },
    ],
};

export const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label: function (context) {
                    return `${context.raw} Task`;
                },
            },
            bodyColor: '#fff',
            titleColor: '#fff',
            displayColors: false,
            backgroundColor: '#000',
        },
    },
    scales: {
        x: { grid: { color: 'rgba(0, 0, 0, 0.05)', }, },
        y: {
            min: 1,
            max: 3,
            ticks: { stepSize: 1 },
            grid: { display: false },
        },
    },
};

export const getCircleData = (percentage) => ({
    datasets: [
        {
            cutout: '92.5%',
            borderWidth: 0,
            data: [percentage, 100 - percentage],
            backgroundColor: ['#546FFF', '#1F1F1F'],
        },
    ],
});

export const circleOptions = {
    rotation: 0,
    cutout: '92.5%',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { tooltip: { enabled: false } },
};

export const settingsTableHeaders = ['Date', 'Invoice', 'Amount', 'Status', 'Actions'];

export const settingsTableBody = [
    { date: 'Jan 13, 2025', invoice: 'X-543242', amount: '$100', status: 'Paid', actions: ['PDF ', 'DOCX'] },
    { date: 'Feb 13, 2025', invoice: 'X-543243', amount: '$100', status: 'Paid', actions: ['PDF ', 'DOCX'] },
    { date: 'Mar 13, 2025', invoice: 'X-543244', amount: '$100', status: 'Paid', actions: ['PDF ', 'DOCX'] },
];

export const paymentMethods = [
    { name: 'Credit Card', icon: 'credit-card' },
    { name: 'PayPal', icon: 'paypal' },
    { name: 'Bank Transfer', icon: 'bank' },
];

export const UserIcon = ({ appMode }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke={appMode === AppMode.LIGHT ? "#4B5563" : "#E5E7EB"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);

export const BillingIcon = ({ appMode }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke={appMode === AppMode.LIGHT ? "#4B5563" : "#E5E7EB"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
        <line x1="1" y1="10" x2="23" y2="10"></line>
    </svg>
);

export const NotificationIcon = ({ appMode }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke={appMode === AppMode.LIGHT ? "#4B5563" : "#E5E7EB"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
    </svg>
);

export const IntegrationIcon = ({ appMode }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke={appMode === AppMode.LIGHT ? "#4B5563" : "#E5E7EB"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
    </svg>
);

export const getFirebaseErrorMessage = (errorCode) => {
    const errorMap = {
        'auth/user-not-found': 'No account found with this email address.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/email-already-in-use': 'This email is already registered. Try logging in instead.',
        'auth/weak-password': 'Password should be at least 6 characters long.',
        'auth/invalid-credential': 'Invalid login credentials. Please try again.',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
        'auth/internal-error': 'Something went wrong. Please try again.',
        'auth/operation-not-allowed': 'This login method is not enabled.',
        'auth/popup-closed-by-user': 'Login popup was closed before completion.',
        'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials.',
    };
    let code = errorCode;
    if (errorCode && errorCode.includes('auth/')) {
        code = errorCode;
    } else if (errorCode && errorCode.includes('Firebase:')) {
        const match = errorCode.match(/\(auth\/[^)]+\)/);
        if (match) {
            code = match[0].replace(/[()]/g, '');
        }
    }
    return errorMap[code] || 'An unexpected error occurred. Please try again.';
};

export const validatePasswordComplexity = (password) => {
    if (password.length < 8) {
        return {
            isValid: false,
            message: "Password must be at least 8 characters long"
        };
    }
    let criteriaCount = 0;
    if (/[A-Z]/.test(password)) criteriaCount++;
    if (/[a-z]/.test(password)) criteriaCount++;
    if (/[0-9]/.test(password)) criteriaCount++;
    if (/[^A-Za-z0-9]/.test(password)) criteriaCount++;
    if (criteriaCount < 3) {
        return {
            isValid: false,
            message: "Password must contain at least 3 of the following: uppercase letters, lowercase letters, numbers, and special characters"
        };
    }

    return { isValid: true, message: "" };
};

export const onboardingSteps = [
    {
        id: 'welcome',
        type: 'info',
        title: "Welcome to Corella",
        description: "Let's personalize your experience with a few quick questions."
    },
    {
        id: 'role',
        type: 'select',
        question: "What industry are you in?",
        options: ["Tech / Software", "Marketing / Advertising", "Consulting / Agency", "Education", "Finance", "Healthcare", "Media / Content", "Nonprofit", "Freelance / Independent", "Other"]
    },
    {
        id: 'experience',
        type: 'select',
        question: "What's your role?",
        options: ["Founder / Executive", "Product Manager", "Designer", "Developer / Engineer", "Marketing / Content", "Other"]
    },
    {
        id: 'interests',
        type: 'multiselect',
        question: "What will you use Corella for?",
        options: ["Project management", "Team collaboration", "Note-taking & docs", "Meeting notes & action items", "Personal productivity", "Other"],
    },
    {
        id: 'complete',
        type: 'info',
        title: "All set!",
        description: "Your profile is ready. Let's get started with Corella."
    }
];

export const signupFeatures = ['Seamless email and calendar integration', 'AI-powered task assistant', 'Real-time team collaboration', 'Unified dashboard for productivity.'];
export const emailDropdownOptions = ['Refresh emails', 'Mark all as read', 'Settings', 'Disconnect account',];

export const policyTopics = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'infoCollection', title: 'Information we collect' },
    { id: 'dataUsage', title: 'How We Use Your Information' },
    { id: 'dataStorage', title: 'Data Storage and Security' },
    { id: 'rightsChoices', title: 'Your Rights and Choices' },
    { id: 'childPrivacy', title: "Children's Privacy" },
    { id: 'policyChanges', title: "Changes to Policy" },
    { id: 'sharingDisclosure', title: "Sharing and Disclosure" },
    { id: 'payments', title: "Payments and Subscriptions" },
    { id: 'cookies', title: "Cookies and Tracking Technologies" },
    { id: 'dataRetention', title: "Data Retention" },
    { id: 'contact', title: "Contact Us" },
];

export const tourSteps = [
    {
        target: '.tour-dashboard',
        content: 'Welcome to your dashboard! This is where you can manage all your tasks, emails, and more in one place.',
        placement: 'center',
        disableBeacon: true,
    },
    {
        target: '.tour-overview',
        content: 'The Overview tab gives you a snapshot of your day, including upcoming events, tasks, and recent emails.',
        disableBeacon: true,
    },
    {
        target: '.tour-tasks',
        content: 'Manage all your tasks here. Create new tasks, mark them as complete, and organize them by priority.',
        disableBeacon: true,
    },
    {
        target: '.tour-emails',
        content: 'Access all your emails in one place. Connect your Google and Microsoft accounts to view and respond to emails.',
        disableBeacon: true,
    },
    {
        target: '.tour-messages',
        content: 'Chat with your team and collaborators directly from this tab.',
        disableBeacon: true,
    },
    {
        target: '.tour-notes',
        content: 'Create and organize notes, ideas, and important information.',
        disableBeacon: true,
    },
    {
        target: '.tour-files',
        content: 'Access and manage all your files from connected accounts.',
        disableBeacon: true,
    },
    {
        target: '.tour-calendar',
        content: 'View and manage your schedule, meetings, and events across all connected accounts.',
        disableBeacon: true,
    },
    {
        target: '.tour-settings',
        content: 'Customize your experience and manage account connections.',
        disableBeacon: true,
    }
];

export const folders = {
    inbox: { displayName: 'Inbox', icon: 'inbox' },
    junk: { displayName: 'Junk Email', icon: 'exclamation-triangle' },
    sent: { displayName: 'Sent Items', icon: 'send' },
    drafts: { displayName: 'Drafts', icon: 'folder' },
    archive: { displayName: 'Archive', icon: 'archive' },
    trash: { displayName: 'Trash', icon: 'trash' }
};

export const INTEREST_OPTIONS = [
    "AI", "Web Development", "Mobile Apps", "Design", "Data Science", "DevOps"
];

export const availableWidgets = [
    { id: 'taskOverview', name: 'Task Overview', description: 'View and manage your tasks' },
    { id: 'weatherWidget', name: 'Weather', description: 'Current weather information' },
    { id: 'notesWidget', name: 'Quick Notes', description: 'Take quick notes' },
    { id: 'calendarWidget', name: 'Mini Calendar', description: 'Compact calendar view' },
    { id: 'stockWidget', name: 'Stock Tracker', description: 'Track your favorite stocks' }
];